<template>
  <div
    class="relative"
    :style="{
      '--page-transition-duration': `${PAGE_TRANSITION_DURATION_MS}ms`,
      minHeight: leavingPageHeight,
    }"
  >
    <!-- The slot gives the transition a single element root: a page's own root is NuxtLayout, which renders a Suspense -->
    <NuxtPage :transition="pageTransition">
      <template #default="{ Component }">
        <div class="page-wrapper">
          <component
            :is="Component"
            ref="pageComponent"
          />
        </div>
      </template>
    </NuxtPage>
  </div>
</template>

<script setup lang="ts">
import type { ComponentPublicInstance, EffectScope } from 'vue'
import { useMediaQuery } from '@vueuse/core'
import { isChangingPage } from '#app/components/utils'

import { PAGE_TRANSITION_DURATION_MS, PAGE_TRANSITION_NAME } from '@layers/web/constants/page-transition.ts'

const pageComponent = useTemplateRef<ComponentPublicInstance>('pageComponent')

/* Keeps the document as tall as the page being left while it fades, or the browser clamps the scroll before the next page's scroll lands */
const leavingPageHeight = ref('')

let frozenPageScopes: EffectScope[] = []

const router = useRouter()
const nuxtApp = useNuxtApp()
const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

/* The pages cross-fade: the one being left floats over the next one until its fade is over */
const pageTransition = computed(() => ({
  name: PAGE_TRANSITION_NAME,
  // The fading elements sit inside the page root, so Vue cannot read the duration off it
  duration: prefersReducedMotion.value ? 0 : PAGE_TRANSITION_DURATION_MS,
  onBeforeLeave: (pageElement: Element) => {
    leavingPageHeight.value = `${(pageElement as HTMLElement).offsetHeight}px`
  },
  onAfterLeave: () => {
    leavingPageHeight.value = ''
  },
  onLeaveCancelled: () => {
    leavingPageHeight.value = ''
  },
}))

const unfreezeCurrentPage = () => {
  unfreezePage(frozenPageScopes)
  frozenPageScopes = []
}

// The next page's setup writes the stores this page renders from; frozen, it keeps showing what it shows
router.beforeEach((to, from) => {
  if (isChangingPage(to, from) && pageComponent.value) {
    frozenPageScopes = freezePage(pageComponent.value)
  }
})

router.afterEach((_to, _from, failure) => {
  if (failure) {
    unfreezeCurrentPage()
  }
})

// A navigation that throws, a chunk that fails to load for instance, never reaches afterEach
router.onError(unfreezeCurrentPage)

// The frozen page has been unmounted by now; this only matters if a navigation kept it
nuxtApp.hook('page:finish', unfreezeCurrentPage)
</script>
